import { useState, useEffect } from 'react';
import { useStoreContext } from '../context/StoreContext';
import { useLocation } from 'react-router-dom';

export function PromotionalPopup() {
  const { store } = useStoreContext();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [isRendered, setIsRendered] = useState(false);

  const popupSettings = store?.settings?.instance_settings?.popup;

  useEffect(() => {
    if (!store || !popupSettings?.enabled || location.pathname !== '/') return;

    const hasSeen = localStorage.getItem(`dilzee_popup_seen_${store.id}`);
    if (hasSeen) {
      const seenTime = parseInt(hasSeen, 10);
      const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
      if (Date.now() - seenTime < sevenDaysMs) {
        return; // Still within 7 days
      }
    }

    // Show popup after 3 seconds on homepage
    const timer = setTimeout(() => {
      setIsOpen(true);
      setIsRendered(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, [popupSettings?.enabled, location.pathname, store?.id]);

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => setIsRendered(false), 300); // Wait for transition
    if (store?.id) {
      localStorage.setItem(`dilzee_popup_seen_${store.id}`, Date.now().toString());
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.dilzee.com';
      const subdomain = window.location.hostname.split('.')[0];
      
      const res = await fetch(`${apiUrl}/${subdomain}/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.error || 'Failed to subscribe');
      }

      setSubmitted(true);
      if (store?.id) {
        localStorage.setItem(`dilzee_popup_seen_${store.id}`, Date.now().toString());
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isRendered || !popupSettings?.enabled) return null;

  return (
    <div 
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        backgroundColor: isOpen ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0)',
        backdropFilter: isOpen ? 'blur(4px)' : 'none',
        transition: 'all 0.3s ease',
        pointerEvents: isOpen ? 'auto' : 'none'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div 
        style={{
          background: popupSettings.background_color || 'white',
          borderRadius: popupSettings.shape === 'square' ? '0' : '16px',
          overflow: 'hidden',
          width: '100%',
          maxWidth: popupSettings.template_style === 'side' ? '700px' : '440px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          transform: isOpen ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)',
          opacity: isOpen ? 1 : 0,
          transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
          position: 'relative',
          display: popupSettings.template_style === 'side' ? 'flex' : 'block'
        }}
      >
        {popupSettings.shape === 'zigzag' && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '8px', background: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(0,0,0,0.05) 10px, rgba(0,0,0,0.05) 20px)' }} />
        )}
        <button 
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            background: 'rgba(255,255,255,0.8)',
            border: 'none',
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            fontSize: '18px',
            color: '#333',
            zIndex: 10
          }}
        >
          ×
        </button>

        {popupSettings.template_style !== 'minimal' && popupSettings.image_url && (
          popupSettings.template_style === 'side' ? (
            <div style={{ 
              width: '45%', 
              flexShrink: 0,
              backgroundImage: `url(${popupSettings.image_url})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }} />
          ) : (
            <img src={popupSettings.image_url} alt="Promotion" style={{ 
              width: '100%', 
              height: '200px', 
              objectFit: 'cover'
            }} />
          )
        )}
        
        <div style={{ padding: '36px 32px', textAlign: 'center', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          {!submitted ? (
            <>
              <h3 style={{ 
                fontFamily: popupSettings.headline_font || 'inherit',
                fontSize: popupSettings.headline_size ? `${popupSettings.headline_size}px` : '26px', 
                fontWeight: 800, 
                color: popupSettings.text_color || 'var(--text-primary)', 
                marginBottom: '12px', 
                lineHeight: 1.2 
              }}>
                {popupSettings.headline || 'Unlock your discount'}
              </h3>
              <p style={{ 
                fontFamily: popupSettings.subheadline_font || 'inherit',
                fontSize: popupSettings.subheadline_size ? `${popupSettings.subheadline_size}px` : '15px', 
                color: popupSettings.text_color ? `${popupSettings.text_color}cc` : 'var(--text-secondary)', 
                marginBottom: '28px', 
                lineHeight: 1.5 
              }}>
                {popupSettings.subheadline || 'Enter your email to get a special discount code.'}
              </p>
              
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <input
                  type="email"
                  required
                  placeholder="Enter your email..."
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={{ 
                    height: '52px', 
                    background: '#f9fafb', 
                    border: '1px solid #e5e7eb', 
                    borderRadius: '8px', 
                    padding: '0 16px',
                    fontSize: '15px',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                />
                {error && <div style={{ color: '#ef4444', fontSize: '13px', textAlign: 'left', fontWeight: 500 }}>{error}</div>}
                
                <button 
                  type="submit" 
                  disabled={loading}
                  style={{ 
                    height: '52px', 
                    background: popupSettings.button_color || 'var(--brand)', 
                    color: popupSettings.button_text_color || 'white', 
                    border: 'none', 
                    borderRadius: popupSettings.shape === 'square' ? '0' : '8px', 
                    fontWeight: 700,
                    fontSize: '16px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.7 : 1,
                    width: '100%'
                  }}
                >
                  {loading ? 'Subscribing...' : (popupSettings.cta_text || 'Subscribe')}
                </button>
              </form>
              
              <button 
                onClick={handleClose} 
                style={{ 
                  marginTop: '16px', 
                  background: 'none', 
                  border: 'none', 
                  color: 'var(--text-tertiary)', 
                  fontSize: '13px', 
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                No thanks, I prefer paying full price
              </button>
            </>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', padding: '16px 0' }}>
              <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#10b981', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px' }}>
                ✓
              </div>
              <h3 style={{ fontSize: '24px', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>
                You're In!
              </h3>
              
              {popupSettings.discount_code ? (
                <>
                  <p style={{ fontSize: '15px', color: 'var(--text-secondary)' }}>
                    Use the code below at checkout to claim your offer:
                  </p>
                  <div style={{ 
                    background: '#f9fafb', 
                    border: '2px dashed #cbd5e1', 
                    borderRadius: '12px', 
                    padding: '16px 24px',
                    fontSize: '24px',
                    fontWeight: 800,
                    color: 'var(--text-primary)',
                    letterSpacing: '2px',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}>
                    {popupSettings.discount_code}
                  </div>
                </>
              ) : (
                <p style={{ fontSize: '15px', color: 'var(--text-secondary)' }}>
                  Thank you for subscribing. We've securely saved your email.
                </p>
              )}
              
              <button 
                onClick={handleClose} 
                style={{ 
                  marginTop: '20px', 
                  height: '48px', 
                  width: '100%',
                  background: popupSettings.button_color || 'var(--text-primary)', 
                  color: popupSettings.button_text_color || 'white', 
                  border: 'none', 
                  borderRadius: popupSettings.shape === 'square' ? '0' : '8px', 
                  fontWeight: 600,
                  fontSize: '15px',
                  cursor: 'pointer',
                }}
              >
                Continue Shopping
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
